import { useState, useRef, useCallback } from 'react';
import { PlainButton } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useMountEffect } from '@/hooks/use-mount-effect';
import { GameModal } from './game-modal';
import { drawSprite, CHURN_MONSTER, UNSUB_GREMLIN, BOUNCE_DEMON } from './sprites';
import * as styles from './styles.css';

export function MessageInvadersCard() {
  const { track } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Read high score from localStorage on mount (avoids sync read during render)
  useMountEffect(() => {
    try {
      const stored = localStorage.getItem('messageInvadersHigh');
      if (stored !== null) {
        setHighScore(Number.parseInt(stored, 10));
      }
    } catch {
      // localStorage may be unavailable in some privacy modes
    }
  });

  // Animate preview sprites only when card is visible
  useMountEffect(() => {
    const canvas = canvasRef.current;
    const card = cardRef.current;
    if (canvas == null || card == null) return;
    const context = canvas.getContext('2d');
    if (context == null) return;

    let frameId = 0;
    let isVisible = false;
    const sprites = [CHURN_MONSTER, UNSUB_GREMLIN, BOUNCE_DEMON];

    function animate(t: number) {
      context!.clearRect(0, 0, canvas!.width, canvas!.height);
      for (let index = 0; index < sprites.length; index++) {
        const bobble = Math.sin(t * 0.003 + index * 1.5) * 3;
        drawSprite(context!, sprites[index], index * 32, 4 + bobble, 2);
      }
      frameId = isVisible ? requestAnimationFrame(animate) : 0;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible && frameId === 0) {
          frameId = requestAnimationFrame(animate);
        }
      },
      { threshold: 0 },
    );
    observer.observe(card);

    return () => {
      observer.disconnect();
      if (frameId !== 0) {
        cancelAnimationFrame(frameId);
      }
    };
  });

  const handleGameOver = useCallback(
    (score: number, wave: number, isNewHighScore: boolean) => {
      track('home', 'messageInvadersGameOver', { Score: score, Wave: wave, 'Is New High Score': isNewHighScore });
    },
    [track],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    track('home', 'messageInvadersOpened', {});
  }, [track]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Refresh high score when modal closes
    try {
      const stored = localStorage.getItem('messageInvadersHigh');
      if (stored !== null) {
        setHighScore(Number.parseInt(stored, 10));
      }
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  return (
    <>
      <div ref={cardRef} className={styles.card}>
        <div className={styles.cardContent}>
          <div className={styles.cardLeft}>
            <div className={styles.cardTitle}>Message Invaders</div>
            <div className={styles.cardSubtitle}>Defend your audience from the forces of churn</div>
            {highScore > 0 ? (
              <div className={styles.cardHighScore}>HIGH SCORE: {highScore.toLocaleString()}</div>
            ) : null}
          </div>
          <div className={styles.cardSprites}>
            <canvas ref={canvasRef} width={96} height={32} style={{ imageRendering: 'pixelated' }} />
            <PlainButton className={styles.playBadge} onClick={handleOpen}>
              Play
            </PlainButton>
          </div>
        </div>
      </div>

      {isOpen ? <GameModal onClose={handleClose} onGameOver={handleGameOver} /> : null}
    </>
  );
}
